import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import axios from 'axios';
import { API_BASE } from '../../lib/utils';
import { toast } from 'sonner';
import {
  ArrowRight, ArrowLeft, CheckCircle, User, GraduationCap,
  Briefcase, Wrench, Heart, Trophy, Compass, Eye, Printer, ChevronRight, Zap,
} from 'lucide-react';
import {
  LC_SUBJECTS, JC_SUBJECTS, LC_GRADES, JC_GRADES,
  DIGITAL_SKILLS, PRACTICAL_SKILLS, SOFT_SKILLS,
  INTERESTS_LIST, INVOLVEMENT_LEVELS, ACHIEVEMENT_PROMPTS,
  LEADERSHIP_ROLES, WORK_VALUES, FUTURE_GOALS, WORK_ENVIRONMENTS,
  WORK_TASKS, COUNTIES,
} from '../../data/resumeOptions';

const SECTIONS = [
  { id: 'personal', label: 'Personal Details', icon: User },
  { id: 'education', label: 'Education', icon: GraduationCap },
  { id: 'work', label: 'Work & Volunteering', icon: Briefcase },
  { id: 'skills', label: 'Skills', icon: Wrench },
  { id: 'interests', label: 'Sport & Interests', icon: Heart },
  { id: 'achievements', label: 'Achievements', icon: Trophy },
  { id: 'values', label: 'Values & Aspirations', icon: Compass },
];

const Chip = ({ label, selected, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled && !selected}
    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
      selected
        ? 'bg-[#0071e3] text-white border-[#0071e3]'
        : disabled
        ? 'bg-[#f5f5f7] text-[#d2d2d7] border-[#e8e8ed] cursor-not-allowed'
        : 'bg-white text-[#1d1d1f] border-[#d2d2d7] hover:border-[#86868b]'
    }`}
    data-testid={`chip-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
  >
    {label}
  </button>
);

const FieldLabel = ({ label, hint }) => (
  <div className="mb-2">
    <label className="text-sm font-semibold text-[#1d1d1f]">{label}</label>
    {hint && <p className="text-xs text-[#86868b] mt-0.5">{hint}</p>}
  </div>
);

const ResumeCompiler = () => {
  const { user } = useAuth();
  const [currentSection, setCurrentSection] = useState(0);
  const [data, setData] = useState({});
  const [completedSections, setCompletedSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const fetchResume = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/resume/${user.id}`);
      if (res.data.sections && Object.keys(res.data.sections).length > 0) {
        setData(res.data.sections);
        setCompletedSections(res.data.completed_sections || []);
        if (res.data.is_complete) setShowPreview(true);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [user.id]);

  useEffect(() => { fetchResume(); }, [fetchResume]);

  const updateField = (section, field, value) => {
    setData(prev => ({
      ...prev,
      [section]: { ...(prev[section] || {}), [field]: value }
    }));
  };

  const toggleChip = (section, field, value, max) => {
    setData(prev => {
      const current = prev[section]?.[field] || [];
      if (current.includes(value)) {
        return { ...prev, [section]: { ...prev[section], [field]: current.filter(v => v !== value) } };
      }
      if (max && current.length >= max) return prev;
      return { ...prev, [section]: { ...prev[section], [field]: [...current, value] } };
    });
  };

  const saveProgress = async (markComplete = false) => {
    setSaving(true);
    const sectionId = SECTIONS[currentSection].id;
    const updated = markComplete && !completedSections.includes(sectionId)
      ? [...completedSections, sectionId] : completedSections;
    try {
      await axios.post(`${API_BASE}/resume/save`, {
        user_id: user.id, sections: data, completed_sections: updated,
      });
      setCompletedSections(updated);
      if (markComplete) toast.success(`${SECTIONS[currentSection].label} saved!`);
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const goNext = async () => {
    await saveProgress(true);
    if (currentSection < SECTIONS.length - 1) {
      setCurrentSection(currentSection + 1);
    } else {
      setShowPreview(true);
    }
  };

  const goPrev = () => {
    if (currentSection > 0) setCurrentSection(currentSection - 1);
  };

  const s = (section) => data[section] || {};

  const [quickRunning, setQuickRunning] = useState(false);
  const handleQuickComplete = async () => {
    setQuickRunning(true);
    try {
      const res = await axios.post(`${API_BASE}/demo/quick-complete-resume/${user.id}`);
      toast.success('Resume auto-completed!');
      setData(res.data.resume);
      setCompletedSections(SECTIONS.map(s => s.id));
      setShowPreview(true);
    } catch (error) {
      toast.error('Quick complete failed');
    } finally {
      setQuickRunning(false);
    }
  };

  if (loading) {
    return <div className="p-12 flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0071e3]" /></div>;
  }

  // =========== PREVIEW / PRINT VIEW ===========
  if (showPreview) {
    return <ResumePreview data={data} onEdit={(idx) => { setShowPreview(false); setCurrentSection(idx); }} />;
  }

  const sec = SECTIONS[currentSection];
  const progress = ((completedSections.length) / SECTIONS.length) * 100;

  return (
    <div className="p-4 sm:p-8 md:p-12">
      <div className="max-w-3xl mx-auto">
        {/* Demo Quick Complete Banner */}
        <div className="bg-[#FFF8E1] border border-[#FFE082] rounded-2xl p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="resume-demo-banner">
          <div className="text-sm text-[#1d1d1f]">
            <span className="font-semibold">Investor Demo:</span> Skip to see a completed resume instantly
          </div>
          <Button
            onClick={handleQuickComplete}
            disabled={quickRunning}
            className="bg-[#FF9500] hover:bg-[#E68600] text-white rounded-full px-5 text-sm"
            data-testid="quick-complete-resume-btn"
          >
            <Zap className="w-3.5 h-3.5 mr-1.5" />
            {quickRunning ? 'Completing...' : 'Quick Complete'}
          </Button>
        </div>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl sm:text-4xl font-semibold text-[#1d1d1f] mb-1">Build Your Resume</h1>
          <p className="text-base text-[#86868b]">Answer the questions below to create your standardised resume. No typing needed for most questions.</p>
        </div>

        {/* Progress */}
        <div className="bg-white rounded-2xl border border-[#d2d2d7] p-4 mb-6">
          <div className="flex items-center justify-between text-sm text-[#86868b] mb-2">
            <span className="font-medium text-[#1d1d1f]">{sec.label}</span>
            <span>Section {currentSection + 1} of {SECTIONS.length}</span>
          </div>
          <div className="w-full h-2 bg-[#f5f5f7] rounded-full overflow-hidden">
            <div className="h-full bg-[#0071e3] rounded-full transition-all duration-500" style={{ width: `${Math.max(progress, ((currentSection + 1) / SECTIONS.length) * 100)}%` }} />
          </div>
          <div className="flex gap-1 mt-3">
            {SECTIONS.map((s, i) => (
              <button key={s.id} onClick={() => { saveProgress(); setCurrentSection(i); }}
                className={`flex-1 h-1 rounded-full transition-all ${i <= currentSection ? 'bg-[#0071e3]' : completedSections.includes(s.id) ? 'bg-green-400' : 'bg-[#e8e8ed]'}`}
                data-testid={`section-dot-${s.id}`} />
            ))}
          </div>
        </div>

        {/* Section Content */}
        <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6 sm:p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#0071e315] flex items-center justify-center">
              <sec.icon className="w-5 h-5 text-[#0071e3]" />
            </div>
            <h2 className="text-xl font-semibold text-[#1d1d1f]">{sec.label}</h2>
          </div>

          {currentSection === 0 && <PersonalSection data={s('personal')} update={(f, v) => updateField('personal', f, v)} />}
          {currentSection === 1 && <EducationSection data={s('education')} update={(f, v) => updateField('education', f, v)} />}
          {currentSection === 2 && <WorkSection data={s('work')} update={(f, v) => updateField('work', f, v)} toggle={(f, v) => toggleChip('work', f, v)} />}
          {currentSection === 3 && <SkillsSection data={s('skills')} toggle={(f, v, m) => toggleChip('skills', f, v, m)} />}
          {currentSection === 4 && <InterestsSection data={s('interests')} update={(f, v) => updateField('interests', f, v)} toggle={(f, v) => toggleChip('interests', f, v)} />}
          {currentSection === 5 && <AchievementsSection data={s('achievements')} update={(f, v) => updateField('achievements', f, v)} toggle={(f, v) => toggleChip('achievements', f, v)} />}
          {currentSection === 6 && <ValuesSection data={s('values')} update={(f, v) => updateField('values', f, v)} toggle={(f, v, m) => toggleChip('values', f, v, m)} />}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button onClick={goPrev} disabled={currentSection === 0} variant="ghost" className="rounded-full text-[#86868b]">
            <ArrowLeft className="w-4 h-4 mr-2" /> Previous
          </Button>
          <Button onClick={goNext} disabled={saving} className="bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-full px-8" data-testid="resume-next-btn">
            {saving ? 'Saving...' : currentSection === SECTIONS.length - 1 ? 'Preview Resume' : 'Save & Continue'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// ===== SECTION COMPONENTS =====

const PersonalSection = ({ data, update }) => (
  <div className="space-y-5">
    <div className="grid sm:grid-cols-2 gap-4">
      <div><FieldLabel label="First Name" hint="As it appears on official documents" /><Input value={data.firstName || ''} onChange={e => update('firstName', e.target.value)} placeholder="e.g. Sean" data-testid="input-first-name" /></div>
      <div><FieldLabel label="Last Name" /><Input value={data.lastName || ''} onChange={e => update('lastName', e.target.value)} placeholder="e.g. Murphy" data-testid="input-last-name" /></div>
    </div>
    <div className="grid sm:grid-cols-2 gap-4">
      <div><FieldLabel label="Email" /><Input type="email" value={data.email || ''} onChange={e => update('email', e.target.value)} placeholder="e.g. sean@email.com" data-testid="input-email" /></div>
      <div><FieldLabel label="Phone Number" /><Input value={data.phone || ''} onChange={e => update('phone', e.target.value)} placeholder="e.g. 087 123 4567" data-testid="input-phone" /></div>
    </div>
    <div className="grid sm:grid-cols-2 gap-4">
      <div><FieldLabel label="Date of Birth" /><Input type="date" value={data.dob || ''} onChange={e => update('dob', e.target.value)} data-testid="input-dob" /></div>
      <div>
        <FieldLabel label="County" hint="Where you currently live" />
        <select value={data.county || ''} onChange={e => update('county', e.target.value)} className="w-full border border-[#d2d2d7] rounded-lg px-3 py-2 text-sm" data-testid="select-county">
          <option value="">Select county...</option>
          {COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
    </div>
    <div className="grid sm:grid-cols-2 gap-4">
      <div>
        <FieldLabel label="Driving Licence" />
        <div className="flex gap-2">
          {['Full Licence', 'Provisional', 'No Licence'].map(opt => (
            <Chip key={opt} label={opt} selected={data.licence === opt} onClick={() => update('licence', opt)} />
          ))}
        </div>
      </div>
      <div>
        <FieldLabel label="Own Transport" hint="Do you have access to your own vehicle?" />
        <div className="flex gap-2">
          {['Yes', 'No'].map(opt => (
            <Chip key={opt} label={opt} selected={data.transport === opt} onClick={() => update('transport', opt)} />
          ))}
        </div>
      </div>
    </div>
  </div>
);

const EducationSection = ({ data, update }) => {
  const addSubjectGrade = (examType, subject) => {
    const key = `${examType}Results`;
    const current = data[key] || [];
    if (current.find(r => r.subject === subject)) return;
    update(key, [...current, { subject, grade: '' }]);
  };
  const updateGrade = (examType, subject, grade) => {
    const key = `${examType}Results`;
    const current = data[key] || [];
    update(key, current.map(r => r.subject === subject ? { ...r, grade } : r));
  };
  const removeSubject = (examType, subject) => {
    const key = `${examType}Results`;
    update(key, (data[key] || []).filter(r => r.subject !== subject));
  };

  return (
    <div className="space-y-6">
      <div><FieldLabel label="School/College Name" hint="Most recent school or college you attended" /><Input value={data.school || ''} onChange={e => update('school', e.target.value)} placeholder="e.g. St. Patrick's CBS, Kilkenny" data-testid="input-school" /></div>
      <div>
        <FieldLabel label="Highest Level Completed" />
        <div className="flex flex-wrap gap-2">
          {['Junior Cert', 'Leaving Cert', 'PLC/QQI', 'Degree', 'Other'].map(opt => (
            <Chip key={opt} label={opt} selected={data.level === opt} onClick={() => update('level', opt)} />
          ))}
        </div>
      </div>

      {(data.level === 'Junior Cert' || data.level === 'Leaving Cert' || data.level === 'PLC/QQI' || data.level === 'Degree') && (
        <>
          {/* Junior Cert */}
          <div>
            <FieldLabel label="Junior Cert Subjects" hint="Tap subjects you studied, then select your grade" />
            <div className="flex flex-wrap gap-1.5 mb-3">
              {JC_SUBJECTS.map(sub => (
                <Chip key={sub} label={sub} selected={(data.jcResults || []).some(r => r.subject === sub)} onClick={() => {
                  if ((data.jcResults || []).some(r => r.subject === sub)) removeSubject('jc', sub);
                  else addSubjectGrade('jc', sub);
                }} />
              ))}
            </div>
            {(data.jcResults || []).length > 0 && (
              <div className="space-y-2 bg-[#f5f5f7] rounded-xl p-3">
                {(data.jcResults || []).map(r => (
                  <div key={r.subject} className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-[#1d1d1f] flex-1">{r.subject}</span>
                    <select value={r.grade} onChange={e => updateGrade('jc', r.subject, e.target.value)} className="border border-[#d2d2d7] rounded-lg px-2 py-1 text-sm w-40">
                      <option value="">Grade...</option>
                      {JC_GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Leaving Cert */}
          {data.level !== 'Junior Cert' && (
            <div>
              <FieldLabel label="Leaving Cert Subjects" hint="Tap subjects you studied, then select your grade (H = Higher, O = Ordinary)" />
              <div className="flex flex-wrap gap-1.5 mb-3">
                {LC_SUBJECTS.map(sub => (
                  <Chip key={sub} label={sub} selected={(data.lcResults || []).some(r => r.subject === sub)} onClick={() => {
                    if ((data.lcResults || []).some(r => r.subject === sub)) removeSubject('lc', sub);
                    else addSubjectGrade('lc', sub);
                  }} />
                ))}
              </div>
              {(data.lcResults || []).length > 0 && (
                <div className="space-y-2 bg-[#f5f5f7] rounded-xl p-3">
                  {(data.lcResults || []).map(r => (
                    <div key={r.subject} className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-[#1d1d1f] flex-1">{r.subject}</span>
                      <select value={r.grade} onChange={e => updateGrade('lc', r.subject, e.target.value)} className="border border-[#d2d2d7] rounded-lg px-2 py-1 text-sm w-28">
                        <option value="">Grade...</option>
                        {LC_GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <div><FieldLabel label="Awards or Distinctions" hint="e.g. Student of the Year, Gaisce Award, subject prize (optional)" /><Input value={data.awards || ''} onChange={e => update('awards', e.target.value)} placeholder="e.g. Gaisce Bronze Award, Maths Student of the Year" data-testid="input-awards" /></div>
    </div>
  );
};

const WorkSection = ({ data, update, toggle }) => (
  <div className="space-y-6">
    <div>
      <FieldLabel label="Do you have any work experience?" hint="This includes part-time jobs, summer work, family business, or TY placements" />
      <div className="flex gap-2">
        {['Yes', 'No'].map(opt => <Chip key={opt} label={opt} selected={data.hasWork === opt} onClick={() => update('hasWork', opt)} />)}
      </div>
    </div>

    {data.hasWork === 'Yes' && (
      <div className="space-y-4">
        {[0, 1, 2, 3].map(i => {
          const role = data[`role${i}`] || {};
          if (i > 0 && !data[`role${i - 1}`]?.title) return null;
          return (
            <div key={i} className="bg-[#f5f5f7] rounded-xl p-4 space-y-3">
              <p className="text-xs font-semibold text-[#86868b] uppercase">Role {i + 1}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input placeholder="Job Title (e.g. Shop Assistant)" value={role.title || ''} onChange={e => update(`role${i}`, { ...role, title: e.target.value })} />
                <Input placeholder="Employer (e.g. Centra, Dunnes)" value={role.employer || ''} onChange={e => update(`role${i}`, { ...role, employer: e.target.value })} />
              </div>
              <Input placeholder="Duration (e.g. June 2024 - Sept 2024)" value={role.duration || ''} onChange={e => update(`role${i}`, { ...role, duration: e.target.value })} />
              <div>
                <p className="text-xs text-[#86868b] mb-1.5">What did you do? (select all that apply)</p>
                <div className="flex flex-wrap gap-1.5">
                  {WORK_TASKS.map(task => (
                    <Chip key={task} label={task} selected={(role.tasks || []).includes(task)}
                      onClick={() => {
                        const current = role.tasks || [];
                        const updated = current.includes(task) ? current.filter(t => t !== task) : [...current, task];
                        update(`role${i}`, { ...role, tasks: updated });
                      }} />
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    )}

    <div>
      <FieldLabel label="Any volunteering experience?" hint="e.g. coaching a team, Tidy Towns, helping at an event, charity work" />
      <div className="flex gap-2 mb-3">
        {['Yes', 'No'].map(opt => <Chip key={opt} label={opt} selected={data.hasVolunteer === opt} onClick={() => update('hasVolunteer', opt)} />)}
      </div>
      {data.hasVolunteer === 'Yes' && (
        <Input placeholder="Describe briefly (e.g. Coached U12 hurling for 2 years, helped at local food bank)" value={data.volunteerDesc || ''} onChange={e => update('volunteerDesc', e.target.value)} data-testid="input-volunteer" />
      )}
    </div>
  </div>
);

const SkillsSection = ({ data, toggle }) => {
  const dCount = (data.digital || []).length;
  const pCount = (data.practical || []).length;
  const sCount = (data.soft || []).length;
  return (
    <div className="space-y-6">
      <div>
        <FieldLabel label={`Digital Skills (pick your top 5)`} hint={`Select the digital skills you're most confident in. ${5 - dCount} remaining.`} />
        <div className="flex flex-wrap gap-1.5">{DIGITAL_SKILLS.map(s => <Chip key={s} label={s} selected={(data.digital || []).includes(s)} disabled={dCount >= 5} onClick={() => toggle('digital', s, 5)} />)}</div>
      </div>
      <div>
        <FieldLabel label={`Practical / Hands-On Skills (pick your top 5)`} hint={`Select hands-on skills you have experience with. ${5 - pCount} remaining.`} />
        <div className="flex flex-wrap gap-1.5">{PRACTICAL_SKILLS.map(s => <Chip key={s} label={s} selected={(data.practical || []).includes(s)} disabled={pCount >= 5} onClick={() => toggle('practical', s, 5)} />)}</div>
      </div>
      <div>
        <FieldLabel label={`Soft Skills (pick your top 5)`} hint={`These are personal strengths. Choose the 5 that best describe you. ${5 - sCount} remaining.`} />
        <div className="flex flex-wrap gap-1.5">{SOFT_SKILLS.map(s => <Chip key={s} label={s} selected={(data.soft || []).includes(s)} disabled={sCount >= 5} onClick={() => toggle('soft', s, 5)} />)}</div>
      </div>
    </div>
  );
};

const InterestsSection = ({ data, update, toggle }) => (
  <div className="space-y-6">
    <div>
      <FieldLabel label="Sport, Hobbies & Interests" hint="Select everything you're into — there's no wrong answer here" />
      <div className="flex flex-wrap gap-1.5">{INTERESTS_LIST.map(i => <Chip key={i} label={i} selected={(data.list || []).includes(i)} onClick={() => toggle('list', i)} />)}</div>
    </div>
    {(data.list || []).length > 0 && (
      <div>
        <FieldLabel label="What's your highest level of involvement?" hint="In your main sport or activity" />
        <div className="flex flex-wrap gap-2">{INVOLVEMENT_LEVELS.map(l => <Chip key={l} label={l} selected={data.level === l} onClick={() => update('level', l)} />)}</div>
      </div>
    )}
  </div>
);

const AchievementsSection = ({ data, update, toggle }) => (
  <div className="space-y-6">
    <div>
      <FieldLabel label="Select any achievements that apply to you" hint="These could be from school, sport, community, or personal life. Every achievement counts." />
      <div className="flex flex-wrap gap-1.5">{ACHIEVEMENT_PROMPTS.map(a => <Chip key={a} label={a} selected={(data.achievements || []).includes(a)} onClick={() => toggle('achievements', a)} />)}</div>
    </div>
    <div><FieldLabel label="Tell us more (optional)" hint="Add any detail about your achievements — e.g. 'Won All-Ireland Scor na nOg for music'" /><Input value={data.achievementDetail || ''} onChange={e => update('achievementDetail', e.target.value)} placeholder="e.g. Won county science fair 2024, completed SafePass certificate" /></div>
    <div>
      <FieldLabel label="Any leadership roles?" hint="Even small things count — like being the person others go to for help" />
      <div className="flex flex-wrap gap-1.5">{LEADERSHIP_ROLES.map(r => <Chip key={r} label={r} selected={(data.leadership || []).includes(r)} onClick={() => toggle('leadership', r)} />)}</div>
    </div>
  </div>
);

const ValuesSection = ({ data, update, toggle }) => {
  const vCount = (data.topValues || []).length;
  return (
    <div className="space-y-6">
      <div>
        <FieldLabel label="Personal Statement" hint="Write 3-4 sentences about yourself, your interests, and why you want an apprenticeship. This appears at the top of your CV." />
        <textarea
          value={data.personalStatement || ''}
          onChange={e => update('personalStatement', e.target.value)}
          rows={4}
          className="w-full p-4 border border-[#d2d2d7] rounded-xl text-sm resize-none focus:outline-none focus:border-[#0071e3] placeholder-[#86868b]"
          placeholder="e.g., I am a motivated and reliable school leaver with a strong interest in electrical engineering. I have hands-on experience from summer work and I am eager to develop my skills through a structured apprenticeship programme..."
          data-testid="personal-statement-input"
        />
      </div>
      <div>
        <FieldLabel label={`What matters most to you at work? (pick your top 3)`} hint={`Choose the 3 things that are most important to you in a job. ${3 - vCount} remaining.`} />
        <div className="flex flex-wrap gap-1.5">{WORK_VALUES.map(v => <Chip key={v} label={v} selected={(data.topValues || []).includes(v)} disabled={vCount >= 3} onClick={() => toggle('topValues', v, 3)} />)}</div>
      </div>
      <div>
        <FieldLabel label="Where do you see yourself in 5 years?" hint="Pick as many as feel right — there are no wrong answers" />
        <div className="flex flex-wrap gap-1.5">{FUTURE_GOALS.map(g => <Chip key={g} label={g} selected={(data.goals || []).includes(g)} onClick={() => toggle('goals', g)} />)}</div>
      </div>
      <div>
        <FieldLabel label="What kind of work environment suits you best?" />
        <div className="flex flex-wrap gap-2">{WORK_ENVIRONMENTS.map(e => <Chip key={e} label={e} selected={data.environment === e} onClick={() => update('environment', e)} />)}</div>
      </div>
    </div>
  );
};

// ===== RESUME PREVIEW =====
const ResumePreview = ({ data, onEdit }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [resetting, setResetting] = useState(false);
  const p = data.personal || {};
  const e = data.education || {};
  const w = data.work || {};
  const sk = data.skills || {};
  const int = data.interests || {};
  const ach = data.achievements || {};
  const val = data.values || {};

  const handlePrint = () => window.print();

  const handleReset = async () => {
    setResetting(true);
    try {
      await axios.post(`${API_BASE}/resume/reset/${user.id}`);
      toast.success('Resume reset! Starting from scratch.');
      window.location.reload();
    } catch (err) {
      toast.error('Failed to reset resume');
    } finally {
      setResetting(false);
    }
  };

  // Build contact line with | separators
  const contactParts = [
    p.county,
    p.phone,
    p.email,
    p.dob ? `DOB: ${p.dob}` : null,
  ].filter(Boolean);

  // Build personal statement — prefer hand-written, fall back to auto-generated
  const personalStatement = val.personalStatement?.trim()
    ? val.personalStatement.trim()
    : (val.environment || val.goals?.length > 0
      ? [
          val.environment ? `I am seeking an apprenticeship in a ${val.environment.toLowerCase()} environment.` : '',
          val.goals?.length > 0 ? `My goals include ${val.goals.map(g => g.toLowerCase()).join(', ')}.` : '',
          val.topValues?.length > 0 ? `I value ${val.topValues.map(v => v.toLowerCase()).join(', ')} in my work.` : '',
          'I am highly motivated, reliable and committed to completing a structured apprenticeship programme that combines hands-on experience with formal qualification.',
        ].filter(Boolean).join(' ')
      : null);

  // Collect all skills as flat bullet list
  const allSkills = [
    ...(sk.digital || []),
    ...(sk.practical || []),
    ...(sk.soft || []),
  ];
  if (p.licence) allSkills.push(`${p.licence} Driving Licence`);
  if (p.transport === 'Yes') allSkills.push('Own transport available');

  return (
    <div className="p-4 sm:p-8 md:p-12">
      <div className="max-w-4xl mx-auto">
        {/* Controls - hidden when printing */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4 print:hidden">
          <div>
            <h1 className="text-3xl sm:text-4xl font-semibold text-[#1d1d1f]">Your Resume</h1>
            <p className="text-base text-[#86868b]">Review your standardised resume below</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button onClick={handleReset} disabled={resetting} variant="outline" className="rounded-full border-red-300 text-red-600 hover:bg-red-50" data-testid="reset-resume-btn">
              {resetting ? 'Resetting...' : 'Reset Resume'}
            </Button>
            <Button onClick={() => onEdit(0)} variant="outline" className="rounded-full border-[#d2d2d7]" data-testid="edit-resume-btn">
              <Eye className="w-4 h-4 mr-2" /> Edit
            </Button>
            <Button onClick={handlePrint} className="bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-full" data-testid="print-resume-btn">
              <Printer className="w-4 h-4 mr-2" /> Print Resume
            </Button>
          </div>
        </div>

        {/* Resume Document — styled to match traditional document format */}
        <div className="bg-white border border-[#d2d2d7] rounded-2xl print:rounded-none print:border-0 print:shadow-none overflow-hidden" id="resume-print" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
          <div className="p-8 sm:p-10 print:p-8">

            {/* ===== NAME & CONTACT ===== */}
            <div className="mb-1">
              <h2 className="text-2xl font-bold text-black tracking-wide uppercase" style={{ fontSize: '22px', letterSpacing: '0.5px' }}>
                {p.firstName || 'First'} {p.lastName || 'Last'}
              </h2>
            </div>
            {contactParts.length > 0 && (
              <p className="text-sm text-gray-700 mb-6" style={{ fontSize: '11px' }}>
                {contactParts.join('  |  ')}
              </p>
            )}

            {/* ===== PERSONAL STATEMENT ===== */}
            {personalStatement && (
              <CVSection title="Personal Statement" onEdit={() => onEdit(6)}>
                <p className="text-sm text-gray-800 leading-relaxed" style={{ fontSize: '11px', lineHeight: '1.6' }}>{personalStatement}</p>
              </CVSection>
            )}

            {/* ===== EDUCATION ===== */}
            <CVSection title="Education" onEdit={() => onEdit(1)}>
              {e.school && (
                <div>
                  <div className="flex justify-between items-baseline">
                    <p className="text-sm font-semibold text-black" style={{ fontSize: '11px' }}>{e.school}</p>
                  </div>
                  {e.level && (
                    <p className="text-sm text-gray-800 mt-1" style={{ fontSize: '11px' }}>
                      {e.level}{e.level === 'Leaving Certificate' ? ' (in progress)' : ''}
                    </p>
                  )}
                  {(e.level === 'Leaving Certificate' || e.level === 'College/University' || e.level === 'PLC/Further Education') && (e.lcResults || []).length > 0 && (
                    <ul className="mt-1 ml-4 space-y-0.5">
                      {e.lcResults.map(r => (
                        <li key={r.subject} className="text-sm text-gray-700 list-disc" style={{ fontSize: '11px' }}>
                          {r.subject}{r.grade ? ` — ${r.grade}` : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                  {e.level === 'Junior Certificate' && (e.jcResults || []).length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-800" style={{ fontSize: '11px' }}>Junior Certificate</p>
                      <ul className="mt-0.5 ml-4 space-y-0.5">
                        {e.jcResults.map(r => (
                          <li key={r.subject} className="text-sm text-gray-700 list-disc" style={{ fontSize: '11px' }}>
                            {r.subject}{r.grade ? ` — ${r.grade}` : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {e.awards && (
                    <ul className="mt-1 ml-4">
                      <li className="text-sm text-gray-700 list-disc" style={{ fontSize: '11px' }}>{e.awards}</li>
                    </ul>
                  )}
                </div>
              )}
            </CVSection>

            {/* ===== WORK EXPERIENCE ===== */}
            {w.hasWork === 'Yes' && (
              <CVSection title="Work Experience" onEdit={() => onEdit(2)}>
                {[0, 1, 2, 3].map(i => {
                  const role = w[`role${i}`];
                  if (!role?.title) return null;
                  return (
                    <div key={i} className={i > 0 ? 'mt-4' : ''}>
                      <div className="flex justify-between items-baseline">
                        <p className="text-sm font-semibold text-black" style={{ fontSize: '11px' }}>{role.employer}</p>
                      </div>
                      <p className="text-sm text-gray-800 italic" style={{ fontSize: '11px' }}>{role.title}{role.duration ? ` (${role.duration})` : ''}</p>
                      {(role.tasks || []).length > 0 && (
                        <ul className="mt-1 ml-4 space-y-0.5">
                          {role.tasks.map(t => (
                            <li key={t} className="text-sm text-gray-700 list-disc" style={{ fontSize: '11px' }}>{t}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
                {w.hasVolunteer === 'Yes' && w.volunteerDesc && (
                  <div className="mt-4">
                    <p className="text-sm font-semibold text-black" style={{ fontSize: '11px' }}>Volunteering</p>
                    <ul className="mt-0.5 ml-4">
                      <li className="text-sm text-gray-700 list-disc" style={{ fontSize: '11px' }}>{w.volunteerDesc}</li>
                    </ul>
                  </div>
                )}
              </CVSection>
            )}

            {/* ===== SKILLS & CERTIFICATIONS ===== */}
            {allSkills.length > 0 && (
              <CVSection title="Skills & Certifications" onEdit={() => onEdit(3)}>
                <ul className="ml-4 space-y-0.5">
                  {allSkills.map((s, i) => (
                    <li key={i} className="text-sm text-gray-700 list-disc" style={{ fontSize: '11px' }}>{s}</li>
                  ))}
                </ul>
              </CVSection>
            )}

            {/* ===== INTERESTS & ACTIVITIES ===== */}
            {((int.list || []).length > 0 || (ach.achievements || []).length > 0 || (ach.leadership || []).length > 0) && (
              <CVSection title="Interests & Activities" onEdit={() => onEdit(4)}>
                {(int.list || []).length > 0 && (
                  <ul className="ml-4 space-y-0.5">
                    {int.list.map(item => (
                      <li key={item} className="text-sm text-gray-700 list-disc" style={{ fontSize: '11px' }}>
                        <span className="font-semibold text-black">{item}</span>
                        {int.level ? ` — ${int.level} level` : ''}
                      </li>
                    ))}
                  </ul>
                )}
                {(ach.achievements || []).length > 0 && (
                  <ul className="mt-1 ml-4 space-y-0.5">
                    {ach.achievements.map(a => (
                      <li key={a} className="text-sm text-gray-700 list-disc" style={{ fontSize: '11px' }}>{a}</li>
                    ))}
                  </ul>
                )}
                {ach.achievementDetail && (
                  <ul className="mt-0.5 ml-4">
                    <li className="text-sm text-gray-700 list-disc" style={{ fontSize: '11px' }}>{ach.achievementDetail}</li>
                  </ul>
                )}
                {(ach.leadership || []).length > 0 && (
                  <ul className="mt-1 ml-4 space-y-0.5">
                    {ach.leadership.map(l => (
                      <li key={l} className="text-sm text-gray-700 list-disc" style={{ fontSize: '11px' }}>
                        <span className="font-semibold text-black">Leadership:</span> {l}
                      </li>
                    ))}
                  </ul>
                )}
              </CVSection>
            )}

            {/* ===== REFERENCES ===== */}
            <CVSection title="References">
              <p className="text-sm text-gray-700" style={{ fontSize: '11px' }}>Available on request.</p>
            </CVSection>

          </div>
        </div>

        {/* Continue to next step */}
        <div className="mt-8 bg-blue-50 rounded-2xl border border-blue-200 p-6 text-center print:hidden">
          <h3 className="text-lg font-semibold text-[#1d1d1f] mb-2">Resume complete!</h3>
          <p className="text-sm text-[#86868b] mb-4">Your standardised resume is ready. Continue to the Personality Profile.</p>
          <Button
            onClick={() => navigate('/apprentice/personality')}
            className="bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-full px-8"
            data-testid="continue-to-personality-btn"
          >
            Continue to Personality Profile <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const CVSection = ({ title, children, onEdit }) => (
  <div style={{ marginTop: '36px' }}>
    <div className="flex items-center justify-between mb-1.5">
      <h3 className="text-sm font-bold text-black uppercase tracking-wider" style={{ fontSize: '12px', letterSpacing: '0.8px' }}>{title}</h3>
      {onEdit && <button onClick={onEdit} className="text-xs text-[#0071e3] font-medium print:hidden hover:underline">Edit</button>}
    </div>
    <div className="border-t border-gray-300 pt-3">{children}</div>
  </div>
);

export default ResumeCompiler;
